import { useState, type ReactNode } from 'react';
import { Platform } from 'react-native';
import { GluestackUIProvider } from '@gluestack-ui/themed';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { DialogProvider } from '@/common/context/DialogContext';
import { generateDynamicTheme } from '@/common/theme/themeGenerator';
import { TOAST_PORTAL_POINTER_EVENTS_CSS } from '@/common/styles/toastPortalPointerEventsCss';

const { gluestackConfig } = generateDynamicTheme(null);

function VisualLabProviderTree({ children }: { children: ReactNode }) {
  return (
    <>
      {Platform.OS === 'web' && (
        <style>{TOAST_PORTAL_POINTER_EVENTS_CSS}</style>
      )}
      <DialogProvider>
        {children}
      </DialogProvider>
    </>
  );
}

/**
 * Minimal provider shell for the Visual Lab playground (no tenant auth).
 * The query client sits above Gluestack so dialogs portaled to its overlay
 * host can still use mutation hooks.
 */
export function VisualLabProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <GluestackUIProvider config={gluestackConfig} colorMode="light">
        {Platform.OS === 'web' ? (
          <SafeAreaProvider>
            <VisualLabProviderTree>{children}</VisualLabProviderTree>
          </SafeAreaProvider>
        ) : (
          <VisualLabProviderTree>{children}</VisualLabProviderTree>
        )}
      </GluestackUIProvider>
    </QueryClientProvider>
  );
}
